import React, { useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  WEAPON_KEYWORD_OPTIONS,
  WeaponKeywordUpgradeFeature
} from "@dndModels";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";

interface Props {
  obj: WeaponKeywordUpgradeFeature;
  onChange: (changed: WeaponKeywordUpgradeFeature) => void; 
  disabled: boolean;
}

export default function WeaponKeywordUpgradeFeatureInput({ 
  obj,
  onChange,
  disabled = false 
}: Props) {
  const [ping, setPing] = useState(false);

  const renderFlatUpgrades = () => {
    const thrown = WEAPON_KEYWORD_OPTIONS.find(o => o.text === "Thrown");
    const thrown_id = thrown?.id??"";
    if (thrown_id !== "" && (obj.added_wk_ids.length === 1 && obj.added_wk_ids.includes(thrown_id))) {
      // Adding thrown to a weapon type that normally isn't thrown.  It needs to have ranges
      return (
        <>
          <Grid
            size={{
              xs: 6
            }}>
            <StringBox 
              name="Range (in feet)"
              value={`${obj.range}`}
              type="number"
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_ff = obj;
                new_ff.range = +changed;
                onChange(new_ff);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            <StringBox 
              name="Long Range (in feet)"
              value={`${obj.range2}`}
              type="number"
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_ff = obj;
                new_ff.range2 = +changed;
                onChange(new_ff);
                setPing(!ping);
              }}
            />
          </Grid>
        </>
      );
    } else if (obj.added_wk_ids.length === 0 && obj.base_wk_ids.length < 2) {
      // We're not adding any keywords, so it's just upgrading an existing one.
      const range = WEAPON_KEYWORD_OPTIONS.find(o => o.text === "Range");
      const range_id = range?.id??"";
      if (thrown_id !== "" && range_id !== "" && obj.base_wk_ids.includes(thrown_id) && obj.base_wk_ids.includes(range_id)) {
        // It's upgrading thrown and ranged weapons.
        return (
          <>
            <Grid
              size={{
                xs: 12
              }}>
              Thrown and Ranged Weapon Attacks will not have disadvantage at long range.
            </Grid>
          </>
        );
      } else if (thrown_id !== "" && obj.base_wk_ids.includes(thrown_id)) {
        // It's upgrading thrown weapons.
        return (
          <>
            <Grid
              size={{
                xs: 12
              }}>
              Thrown Weapon Attacks will not have disadvantage at long range.
            </Grid>
          </>
        );
      } else if (range_id !== "" && obj.base_wk_ids.includes(range_id)) {
        // It's upgrading ranged weapons.
        return (
          <>
            <Grid
              size={{
                xs: 12
              }}>
              Ranged Weapon Attacks will not have disadvantage at long range.
            </Grid>
          </>
        );
      }
    }
    return null;
  }

  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 6
        }}>
        <SelectStringBox 
          name="Weapon Keywords to Upgrade" 
          options={WEAPON_KEYWORD_OPTIONS}
          values={obj.base_wk_ids} 
          multiple
          disabled={disabled}
          onChange={(changed: string[]) => {
            const new_ff = obj;
            new_ff.base_wk_ids = changed;
            onChange(new_ff);
            setPing(!ping);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <SelectStringBox 
          name="Excluded Weapon Keywords" 
          options={WEAPON_KEYWORD_OPTIONS}
          values={obj.excluded_wk_ids} 
          multiple
          disabled={disabled}
          onChange={(changed: string[]) => {
            const new_ff = obj;
            new_ff.excluded_wk_ids = changed;
            onChange(new_ff);
            setPing(!ping);
          }} 
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <SelectStringBox 
          name="Weapon Keywords to Add to Selected" 
          options={WEAPON_KEYWORD_OPTIONS}
          values={obj.added_wk_ids} 
          multiple
          disabled={disabled}
          onChange={(changed: string[]) => {
            const new_ff = obj;
            new_ff.added_wk_ids = changed;
            onChange(new_ff);
            setPing(!ping);
          }} 
        />
      </Grid>
      { renderFlatUpgrades() }
    </Grid>
  );
}
